export const HOMEBASE_EXTENSION_MESSAGE_SOURCE = 'bookkeeper-homebase-extension'

export type HomebaseExtensionErrorCode =
  | 'NOT_INSTALLED'
  | 'LOGIN_REQUIRED'
  | 'EXTRACTION_FAILED'
  | 'TIMEOUT'

export type HomebaseExtensionTokens = {
  sessionId: string
  stytchSessionJwt: string
}

export class HomebaseExtensionError extends Error {
  code: HomebaseExtensionErrorCode

  constructor(code: HomebaseExtensionErrorCode, message: string) {
    super(message)
    this.name = 'HomebaseExtensionError'
    this.code = code
  }
}

type ExtensionMessage = {
  source?: string
  type?: string
  requestId?: string
  success?: boolean
  code?: HomebaseExtensionErrorCode
  error?: string
  tokens?: {
    sessionId: string
    stytchSessionJwt: string
  }
}

let extensionReady = false

function isExtensionMessage(data: unknown): data is ExtensionMessage {
  return (
    !!data &&
    typeof data === 'object' &&
    'source' in data &&
    (data as ExtensionMessage).source === HOMEBASE_EXTENSION_MESSAGE_SOURCE
  )
}

function markExtensionReady() {
  extensionReady = true
}

// Catch early READY announcements fired before React mounts.
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
      return
    }
    if (event.data.type === 'HOMEBASE_EXTENSION_READY') {
      markExtensionReady()
    }
  })
}

/**
 * Ping the content script and wait for a READY response.
 */
function probeHomebaseExtension(timeoutMs = 1500): Promise<boolean> {
  if (extensionReady) {
    return Promise.resolve(true)
  }

  const requestId = crypto.randomUUID()

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      resolve(false)
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (event.data.type !== 'HOMEBASE_EXTENSION_READY') {
        return
      }

      if (event.data.requestId && event.data.requestId !== requestId) {
        return
      }

      markExtensionReady()
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      resolve(true)
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: HOMEBASE_EXTENSION_MESSAGE_SOURCE,
        type: 'HOMEBASE_EXTENSION_PING',
        requestId,
      },
      window.location.origin
    )
  })
}

/**
 * Returns true once the Homebase extension content script has announced readiness.
 */
export function isHomebaseExtensionAvailable(timeoutMs = 1500): Promise<boolean> {
  return probeHomebaseExtension(timeoutMs)
}

/**
 * Request Homebase tokens from the installed Chrome extension.
 */
export async function requestHomebaseTokensFromExtension(
  timeoutMs = 15000
): Promise<HomebaseExtensionTokens> {
  const requestId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage)
      reject(
        new HomebaseExtensionError(
          'NOT_INSTALLED',
          `Homebase Token Extractor extension did not respond. Reload the extension at chrome://extensions/, refresh this page (${window.location.origin}), and try again.`
        )
      )
    }, timeoutMs)

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || !isExtensionMessage(event.data)) {
        return
      }

      if (
        event.data.type !== 'HOMEBASE_EXTRACT_TOKENS_RESPONSE' ||
        event.data.requestId !== requestId
      ) {
        return
      }

      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      markExtensionReady()

      if (event.data.success && event.data.tokens) {
        resolve({
          sessionId: event.data.tokens.sessionId,
          stytchSessionJwt: event.data.tokens.stytchSessionJwt,
        })
        return
      }

      const code = event.data.code ?? 'EXTRACTION_FAILED'
      const message =
        event.data.error ??
        (code === 'LOGIN_REQUIRED'
          ? 'Please log into Homebase, then try again.'
          : 'Failed to extract Homebase tokens.')

      reject(new HomebaseExtensionError(code, message))
    }

    window.addEventListener('message', onMessage)

    window.postMessage(
      {
        source: HOMEBASE_EXTENSION_MESSAGE_SOURCE,
        type: 'HOMEBASE_EXTRACT_TOKENS_REQUEST',
        requestId,
      },
      window.location.origin
    )
  })
}
